import { useEffect, useMemo, useState } from "react";
import "./Reviews.css";

import { initializeApp } from "firebase/app";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  getFirestore,
  serverTimestamp,
  updateDoc
} from "firebase/firestore";

const firebaseConfig = {
  apiKey: "AIzaSyAOIKiGMei60YoomiGx4K7tExfZqeyTa1U",
  authDomain: "solarwavetechnology-d2d8a.firebaseapp.com",
  databaseURL: "https://solarwavetechnology-d2d8a-default-rtdb.firebaseio.com",
  projectId: "solarwavetechnology-d2d8a",
  storageBucket: "solarwavetechnology-d2d8a.firebasestorage.app",
  messagingSenderId: "574766647333",
  appId: "1:574766647333:web:e66b97db35cd0e335157cc",
  measurementId: "G-FJR84JQKCR"
};

const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

const ratingOptions = [1, 2, 3, 4, 5];
const filledStar = "\u2b50";
const emptyStar = "\u2606";
const reviewerIdKey = "solarwaveReviewerId";

function getReviewerId() {
  const savedReviewerId = localStorage.getItem(reviewerIdKey);
  if (savedReviewerId) return savedReviewerId;

  const newReviewerId = crypto.randomUUID();
  localStorage.setItem(reviewerIdKey, newReviewerId);
  return newReviewerId;
}

function getRatingNumber(value) {
  if (typeof value === "number") return value;
  if (!value) return 0;

  const parsed = Number(value);
  if (!Number.isNaN(parsed)) return parsed;

  return String(value).split(filledStar).length - 1;
}

function getInitials(name) {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

function getCreatedTime(review) {
  if (!review.createdAt) return 0;
  if (typeof review.createdAt.toMillis === "function") {
    return review.createdAt.toMillis();
  }
  return new Date(review.createdAt).getTime() || 0;
}

function Reviews() {
  const [reviewerId] = useState(getReviewerId);
  const [name, setName] = useState("");
  const [rating, setRating] = useState("");
  const [message, setMessage] = useState("");
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editRating, setEditRating] = useState("");
  const [editMessage, setEditMessage] = useState("");

  const stats = useMemo(() => {
    const total = reviews.length;
    const ratingTotal = reviews.reduce(
      (sum, review) => sum + getRatingNumber(review.rating),
      0
    );
    const average = total ? ratingTotal / total : 0;

    return {
      total,
      average,
      roundedAverage: Math.round(average)
    };
  }, [reviews]);

  const loadReviews = async () => {
    setLoading(true);

    try {
      const querySnapshot = await getDocs(collection(db, "reviews"));
      const allReviews = querySnapshot.docs
        .map((reviewDoc) => ({
          id: reviewDoc.id,
          ...reviewDoc.data()
        }))
        .sort((a, b) => getCreatedTime(b) - getCreatedTime(a));

      setReviews(allReviews);
    } catch (error) {
      console.error(error);
      alert("Error loading reviews");
    } finally {
      setLoading(false);
    }
  };

  const submitReview = async () => {
    if (!name.trim() || !rating || !message.trim()) {
      alert("Please fill all fields");
      return;
    }

    setSubmitting(true);

    try {
      await addDoc(collection(db, "reviews"), {
        name: name.trim(),
        rating: Number(rating),
        message: message.trim(),
        ownerId: reviewerId,
        createdAt: serverTimestamp()
      });

      alert("Review submitted successfully");

      setName("");
      setRating("");
      setMessage("");

      await loadReviews();
    } catch (error) {
      console.error(error);
      alert("Error submitting review");
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (review) => {
    setEditingId(review.id);
    setEditName(review.name || "");
    setEditRating(String(getRatingNumber(review.rating)));
    setEditMessage(review.message || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName("");
    setEditRating("");
    setEditMessage("");
  };

  const saveReview = async (reviewId) => {
    const review = reviews.find((item) => item.id === reviewId);
    if (!review || review.ownerId !== reviewerId) {
      alert("You can only edit your own review");
      return;
    }

    if (!editName.trim() || !editRating || !editMessage.trim()) {
      alert("Please fill all fields");
      return;
    }

    try {
      await updateDoc(doc(db, "reviews", reviewId), {
        name: editName.trim(),
        rating: Number(editRating),
        message: editMessage.trim()
      });

      cancelEdit();
      await loadReviews();
    } catch (error) {
      console.error(error);
      alert("Error updating review");
    }
  };

  const removeReview = async (reviewId) => {
    const review = reviews.find((item) => item.id === reviewId);
    if (!review || review.ownerId !== reviewerId) {
      alert("You can only delete your own review");
      return;
    }

    const shouldDelete = window.confirm("Delete this review?");
    if (!shouldDelete) return;

    try {
      await deleteDoc(doc(db, "reviews", reviewId));
      await loadReviews();
    } catch (error) {
      console.error(error);
      alert("Error deleting review");
    }
  };

  useEffect(() => {
    loadReviews();
  }, []);

  return (
    <main className="reviews-page">
      <section className="reviews-hero">
        <div className="reviews-hero-content">
          <span className="reviews-eyebrow">SolarWave Technology</span>
          <h1>Customer Reviews</h1>
          <p>
            Real feedback from customers who trust our solar solutions for
            cleaner, smarter energy.
          </p>
        </div>

        <div className="reviews-stats" aria-label="Review summary">
          <div className="stat-box">
            <span>{stats.total}</span>
            <p>Total Reviews</p>
          </div>

          <div className="stat-box featured">
            <span>{stats.average.toFixed(1)}</span>
            <p>Overall Rating</p>
            <div
              className="summary-stars"
              aria-label={`${stats.average.toFixed(1)} out of 5`}
            >
              {filledStar.repeat(stats.roundedAverage)}
              {emptyStar.repeat(5 - stats.roundedAverage)}
            </div>
          </div>
        </div>
      </section>

      <section className="reviews-content">
        <div className="review-form">
          <h2>Share Your Experience</h2>

          <label>
            Your Name
            <input
              type="text"
              placeholder="Enter your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </label>

          <label>
            Your Rating
            <select value={rating} onChange={(e) => setRating(e.target.value)}>
              <option value="">Select rating</option>
              {ratingOptions.map((option) => (
                <option value={option} key={option}>
                  {option} Star{option > 1 ? "s" : ""}
                </option>
              ))}
            </select>
          </label>

          <label>
            Your Review
            <textarea
              placeholder="Write your review"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </label>

          <button type="button" onClick={submitReview} disabled={submitting}>
            {submitting ? "Submitting..." : "Submit Review"}
          </button>
        </div>

        <div className="reviews-list">
          {loading ? (
            <div className="empty-state">Loading reviews...</div>
          ) : reviews.length === 0 ? (
            <div className="empty-state">
              No reviews yet. Be the first to share one.
            </div>
          ) : (
            reviews.map((review, index) => {
              const ratingNumber = getRatingNumber(review.rating);
              const isEditing = editingId === review.id;
              const canManageReview = review.ownerId === reviewerId;

              return (
                <article
                  className="review-card"
                  key={review.id}
                  style={{ animationDelay: `${index * 80}ms` }}
                >
                  {isEditing ? (
                    <div className="review-edit-form">
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                      />

                      <select
                        value={editRating}
                        onChange={(e) => setEditRating(e.target.value)}
                      >
                        {ratingOptions.map((option) => (
                          <option value={option} key={option}>
                            {option} Star{option > 1 ? "s" : ""}
                          </option>
                        ))}
                      </select>

                      <textarea
                        value={editMessage}
                        onChange={(e) => setEditMessage(e.target.value)}
                      />

                      <div className="review-actions">
                        <button
                          className="save-btn"
                          type="button"
                          onClick={() => saveReview(review.id)}
                        >
                          Save
                        </button>
                        <button
                          className="cancel-btn"
                          type="button"
                          onClick={cancelEdit}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="review-card-header">
                        <div className="review-avatar">
                          {getInitials(review.name || "Guest")}
                        </div>
                        <div>
                          <h3>{review.name}</h3>
                          <p
                            className="stars"
                            aria-label={`${ratingNumber} out of 5 stars`}
                          >
                            {filledStar.repeat(ratingNumber)}
                            {emptyStar.repeat(5 - ratingNumber)}
                          </p>
                        </div>
                      </div>

                      <p className="review-message">{review.message}</p>

                      {canManageReview && (
                        <div className="review-actions">
                          <button
                            className="edit-btn"
                            type="button"
                            onClick={() => startEdit(review)}
                          >
                            Edit
                          </button>
                          <button
                            className="delete-btn"
                            type="button"
                            onClick={() => removeReview(review.id)}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </article>
              );
            })
          )}
        </div>
      </section>
    </main>
  );
}

export default Reviews;
